import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Upload, Trash2, FileText, X } from 'lucide-react';
import { uploadInvoice, getInvoicesByItem, deleteInvoice, getInvoiceDownloadUrl } from '../api';
import { toast } from '../hooks/use-toast';

const InvoiceGallery = ({ isOpen, onClose, itemId, itemName }) => {
  const [invoices, setInvoices] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && itemId) {
      loadInvoices();
    }
  }, [isOpen, itemId]);

  const loadInvoices = async () => {
    if (!itemId) return;
    
    setLoading(true);
    try {
      const data = await getInvoicesByItem(itemId);
      setInvoices(data);
    } catch (error) {
      console.error('Error loading invoices:', error);
      toast({
        title: 'שגיאה',
        description: 'לא ניתן לטעון חשבוניות',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate PDF
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast({
        title: 'שגיאה',
        description: 'ניתן להעלות קבצי PDF בלבד',
        variant: 'destructive'
      });
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: 'שגיאה',
        description: 'גודל הקובץ חייב להיות קטן מ-10MB',
        variant: 'destructive'
      });
      return;
    }

    setUploading(true);
    try {
      await uploadInvoice(itemId, file);
      toast({
        title: 'הצלחה',
        description: 'החשבונית הועלתה בהצלחה',
      });
      await loadInvoices();
      event.target.value = ''; // Reset input
    } catch (error) {
      console.error('Error uploading invoice:', error);
      toast({
        title: 'שגיאה',
        description: 'לא ניתן להעלות חשבונית',
        variant: 'destructive'
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (invoiceId) => {
    if (!window.confirm('האם אתה בטוח שברצונך למחוק חשבונית זו?')) {
      return;
    }

    try {
      await deleteInvoice(invoiceId);
      toast({
        title: 'הצלחה',
        description: 'החשבונית נמחקה בהצלחה',
      });
      await loadInvoices();
    } catch (error) {
      console.error('Error deleting invoice:', error);
      toast({
        title: 'שגיאה',
        description: 'לא ניתן למחוק חשבונית',
        variant: 'destructive'
      });
    }
  };

  const handleView = (invoiceId) => {
    const url = getInvoiceDownloadUrl(invoiceId);
    window.open(url, '_blank');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" dir="rtl">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-right">
            חשבוניות רכש - {itemName}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Upload Section */}
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
            <input
              type="file"
              id="invoice-upload"
              accept=".pdf"
              onChange={handleFileSelect}
              className="hidden"
              disabled={uploading}
            />
            <label
              htmlFor="invoice-upload"
              className={`flex flex-col items-center gap-2 cursor-pointer ${
                uploading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <Upload className="w-12 h-12 text-gray-400" />
              <div className="text-sm text-gray-600">
                {uploading ? 'מעלה...' : 'לחץ להעלאת חשבונית (PDF)'}
              </div>
              <div className="text-xs text-gray-500">
                גודל מקסימלי: 10MB
              </div>
            </label>
          </div>

          {/* Invoices Grid */}
          {loading ? (
            <div className="text-center py-8 text-gray-500">טוען חשבוניות...</div>
          ) : invoices.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              אין חשבוניות עדיין. העלה חשבונית ראשונה.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {invoices.map((invoice) => (
                <div
                  key={invoice.id}
                  className="border rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start gap-3">
                    <FileText className="w-10 h-10 text-red-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate" title={invoice.fileName}>
                        {invoice.fileName}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {new Date(invoice.uploadDate).toLocaleDateString('he-IL')}
                      </div>
                      <div className="flex gap-2 mt-3">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleView(invoice.id)}
                          className="text-xs"
                        >
                          צפייה
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDelete(invoice.id)}
                          className="text-xs"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Close Button */}
        <div className="flex justify-end mt-4">
          <Button variant="outline" onClick={onClose}>
            סגור
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InvoiceGallery;
